import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../../store";
import type {
  MsaState,
  MsaJobState,
  MsaCalculationResult,
  MsaMode,
  LandCoverChangeResult,
  LandCoverChangeStatus,
} from "./msaTypes";

const initialState: MsaState = {};

/**
 * Guards against prototype-pollution when a value derived from action payloads
 * is used as an object key (e.g. `state[projectId]`). Rejects the reserved keys
 * that could mutate `Object.prototype`.
 */
const isSafeObjectKey = (key: string): boolean =>
  key !== "__proto__" && key !== "constructor" && key !== "prototype";

const msaSlice = createSlice({
  name: "msa",
  initialState,
  reducers: {
    /**
     * Set job ID when calculation starts
     */
    setMsaJobId: (
      state,
      action: PayloadAction<{
        projectId: string;
        mode: MsaMode;
        jobId: string;
      }>
    ) => {
      const { projectId, mode, jobId } = action.payload;

      if (!isSafeObjectKey(projectId)) return;

      // Initialize project entry if it doesn't exist
      if (!state[projectId]) {
        state[projectId] = {
          baseline: null,
          project: null,
        };
      }

      // Initialize or update job state
      state[projectId][mode] = {
        jobId,
        status: "starting",
        progress: 0,
        message: "Initializing calculation...",
        result: null,
        error: null,
      };
    },

    /**
     * Update job progress
     */
    updateMsaProgress: (
      state,
      action: PayloadAction<{
        projectId: string;
        mode: MsaMode;
        progress: number;
        message: string;
      }>
    ) => {
      const { projectId, mode, progress, message } = action.payload;

      if (state[projectId]?.[mode]) {
        state[projectId][mode]!.status = "in_progress";
        state[projectId][mode]!.progress = progress;
        state[projectId][mode]!.message = message;
      }
    },

    /**
     * Mark job as started
     */
    setMsaJobStarted: (
      state,
      action: PayloadAction<{
        projectId: string;
        mode: MsaMode;
        message?: string;
      }>
    ) => {
      const { projectId, mode, message } = action.payload;

      if (state[projectId]?.[mode]) {
        state[projectId][mode]!.status = "in_progress";
        state[projectId][mode]!.message =
          message || "Calculation in progress...";
      }
    },

    /**
     * Complete job with results
     */
    completeMsaCalculation: (
      state,
      action: PayloadAction<{
        projectId: string;
        mode: MsaMode;
        result: MsaCalculationResult;
      }>
    ) => {
      const { projectId, mode, result } = action.payload;

      if (state[projectId]?.[mode]) {
        state[projectId][mode]!.status = "completed";
        state[projectId][mode]!.progress = 100;
        state[projectId][mode]!.message = "Calculation completed successfully";
        state[projectId][mode]!.result = result;
        state[projectId][mode]!.error = null;
      }
    },

    /**
     * Mark job as failed
     */
    failMsaCalculation: (
      state,
      action: PayloadAction<{
        projectId: string;
        mode: MsaMode;
        error: string;
      }>
    ) => {
      const { projectId, mode, error } = action.payload;

      if (state[projectId]?.[mode]) {
        state[projectId][mode]!.status = "failed";
        state[projectId][mode]!.message = "Calculation failed";
        state[projectId][mode]!.error = error;
      }
    },

    /**
     * Clear specific job state
     */
    clearMsaJob: (
      state,
      action: PayloadAction<{
        projectId: string;
        mode: MsaMode;
      }>
    ) => {
      const { projectId, mode } = action.payload;

      if (state[projectId]?.[mode]) {
        state[projectId][mode] = null;
      }
    },

    /**
     * Clear all jobs for a project
     */
    clearMsaProject: (state, action: PayloadAction<{ projectId: string }>) => {
      const { projectId } = action.payload;
      delete state[projectId];
    },

    /**
     * Mark land cover change calculation as in-progress
     */
    setLandCoverChangeCalculating: (
      state,
      action: PayloadAction<{ projectId: string }>
    ) => {
      const { projectId } = action.payload;
      if (state[projectId]) {
        state[projectId].landCoverChangeStatus = "calculating";
        state[projectId].landCoverChangeError = null;
      }
    },

    /**
     * Set land cover change calculation result
     */
    setLandCoverChange: (
      state,
      action: PayloadAction<{
        projectId: string;
        result: LandCoverChangeResult;
      }>
    ) => {
      const { projectId, result } = action.payload;
      if (state[projectId]) {
        state[projectId].landCoverChange = result;
        state[projectId].landCoverChangeStatus = "completed";
        state[projectId].landCoverChangeError = null;
      }
    },

    /**
     * Mark land cover change calculation as failed
     */
    setLandCoverChangeFailed: (
      state,
      action: PayloadAction<{ projectId: string; error: string }>
    ) => {
      const { projectId, error } = action.payload;
      if (state[projectId]) {
        state[projectId].landCoverChangeStatus = "failed";
        state[projectId].landCoverChangeError = error;
      }
    },

    /**
     * Clear land cover change for a project
     */
    clearLandCoverChange: (
      state,
      action: PayloadAction<{ projectId: string }>
    ) => {
      const { projectId } = action.payload;
      if (state[projectId]) {
        state[projectId].landCoverChange = null;
        state[projectId].landCoverChangeStatus = "idle";
        state[projectId].landCoverChangeError = null;
      }
    },
  },
});

// Actions
export const {
  setMsaJobId,
  updateMsaProgress,
  setMsaJobStarted,
  completeMsaCalculation,
  failMsaCalculation,
  clearMsaJob,
  clearMsaProject,
  setLandCoverChangeCalculating,
  setLandCoverChange,
  setLandCoverChangeFailed,
  clearLandCoverChange,
} = msaSlice.actions;

// Selectors
export const selectMsaJobByProject = (
  state: RootState,
  projectId: string,
  mode: MsaMode
): MsaJobState | null => {
  return state.msa[projectId]?.[mode] ?? null;
};

export const selectMsaJobId = (
  state: RootState,
  projectId: string,
  mode: MsaMode
): string | null => {
  return state.msa[projectId]?.[mode]?.jobId ?? null;
};

export const selectAllMsaJobs = (state: RootState): MsaState => {
  return state.msa;
};

export const selectMsaJobsByProject = (
  state: RootState,
  projectId: string
): { baseline: MsaJobState | null; project: MsaJobState | null } | null => {
  return state.msa[projectId] ?? null;
};

export const selectLandCoverChange = (
  state: RootState,
  projectId: string
): LandCoverChangeResult | null | undefined => {
  return state.msa[projectId]?.landCoverChange ?? null;
};

export const selectLandCoverChangeStatus = (
  state: RootState,
  projectId: string
): LandCoverChangeStatus => {
  return state.msa[projectId]?.landCoverChangeStatus ?? "idle";
};

export const selectLandCoverChangeError = (
  state: RootState,
  projectId: string
): string | null => {
  return state.msa[projectId]?.landCoverChangeError ?? null;
};

/**
 * Returns a map of projectId → { landCoverChange, landCoverChangeStatus }
 * for use in the auto-trigger hook that watches all projects at once.
 */
export const selectAllLandCoverChangeData = (
  state: RootState
): Record<
  string,
  {
    landCoverChange?: LandCoverChangeResult | null;
    landCoverChangeStatus?: LandCoverChangeStatus;
  }
> => {
  const result: Record<
    string,
    {
      landCoverChange?: LandCoverChangeResult | null;
      landCoverChangeStatus?: LandCoverChangeStatus;
    }
  > = {};
  for (const [projectId, entry] of Object.entries(state.msa)) {
    result[projectId] = {
      landCoverChange: entry?.landCoverChange,
      landCoverChangeStatus: entry?.landCoverChangeStatus,
    };
  }
  return result;
};

export default msaSlice.reducer;

